import { useState } from 'react';
import { Check, X, Edit2 } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Textarea from '../ui/Textarea';
import HistoricalReportsTimeline from './HistoricalReportsTimeline';

interface CompanyResearchReviewProps {
  researchData: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  isSaving?: boolean;
  caseId?: string;
  companyId?: string;
}

export default function CompanyResearchReview({
  researchData,
  onSave,
  onCancel,
  isSaving = false,
  caseId,
  companyId,
}: CompanyResearchReviewProps) {
  const [editedData, setEditedData] = useState(researchData);
  const [editingSection, setEditingSection] = useState<string | null>(null);

  const handleSectionEdit = (section: string, field: string, value: any) => {
    setEditedData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleArrayEdit = (section: string, field: string, index: number, value: string) => {
    setEditedData((prev: any) => {
      const newArray = [...(prev[section]?.[field] || [])];
      newArray[index] = value;
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray,
        },
      };
    });
  };

  // New handler for editing object fields (for financial data)
  const handleObjectFieldEdit = (section: string, field: string, index: number, fieldName: string, value: string) => {
    setEditedData((prev: any) => {
      const newArray = [...(prev[section]?.[field] || [])];
      const item = newArray[index];
      if (typeof item === 'object') {
        newArray[index] = { ...item, [fieldName]: value };
      } else {
        // Convert string to object if needed
        newArray[index] = { name: item, [fieldName]: value };
      }
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray,
        },
      };
    });
  };

  const handleArrayAdd = (section: string, field: string) => {
    let newItem;
    if (field === 'businessUnits') {
      newItem = { name: '', revenue: '', operatingIncome: '', grossMargin: '', operatingMargin: '' };
    } else if (field === 'productLines') {
      newItem = { name: '', revenue: '', revenuePercentage: '', growthRate: '' };
    } else if (field === 'geographicFootprint' || field === 'geographicRegions') {
      newItem = { name: '', revenue: '', revenuePercentage: '', growthRate: '', operatingIncome: '' };
    } else {
      newItem = '';
    }

    setEditedData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: [...(prev[section]?.[field] || []), newItem],
      },
    }));
  };

  const handleArrayRemove = (section: string, field: string, index: number) => {
    setEditedData((prev: any) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: (prev[section]?.[field] || []).filter((_: any, i: number) => i !== index),
      },
    }));
  };

  const extractValue = (data: any): string => {
    if (typeof data === 'string') return data;
    if (data?.value) return data.value;
    return JSON.stringify(data);
  };

  return (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Company Research Results</h3>
        <p className="text-blue-700 text-sm">
          Review the company research results below. You can edit any field before saving to the database.
        </p>
      </div>

      {/* Historical Reports Timeline */}
      {caseId && companyId && (
        <HistoricalReportsTimeline
          caseId={ caseId}
          companyId={companyId}
          companyName={editedData.companyName || ''}
          ticker={editedData.ticker || ''}
        />
      )}

      {/* Financial Profile */}
      {editedData.financialProfile && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">💰 Financial Profile</h4>
            <button
              onClick={() => setEditingSection(editingSection === 'financial' ? null : 'financial')}
              className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
            >
              {editingSection === 'financial' ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              {editingSection === 'financial' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="space-y-3">
            {editingSection === 'financial' ? (
              <>
                <Input
                  label="Revenue"
                  value={extractValue(editedData.financialProfile.revenue)}
                  onChange={(e) => handleSectionEdit('financialProfile', 'revenue', e.target.value)}
                />
                <Input
                  label="Revenue Period"
                  value={editedData.financialProfile.revenuePeriod || editedData.financialProfile.revenue?.period || ''}
                  onChange={(e) => handleSectionEdit('financialProfile', 'revenuePeriod', e.target.value)}
                />
                <Input
                  label="Margins"
                  value={extractValue(editedData.financialProfile.margins)}
                  onChange={(e) => handleSectionEdit('financialProfile', 'margins', e.target.value)}
                />
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Revenue Segments</label>
                  {(editedData.financialProfile.revenueSegments || []).map((seg: any, idx: number) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <Input
                        placeholder="Segment name"
                        value={typeof seg === 'string' ? seg : seg.segment}
                        onChange={(e) => handleArrayEdit('financialProfile', 'revenueSegments', idx, e.target.value)}
                      />
                      <button
                        onClick={() => handleArrayRemove('financialProfile', 'revenueSegments', idx)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleArrayAdd('financialProfile', 'revenueSegments')}
                  >
                    Add Segment
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Revenue:</span>
                    <p className="text-neutral-900">{extractValue(editedData.financialProfile.revenue)}</p>
                    {editedData.financialProfile.revenue?.period && (
                      <p className="text-sm text-neutral-500">{editedData.financialProfile.revenue.period}</p>
                    )}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Margins:</span>
                    <p className="text-neutral-900">{extractValue(editedData.financialProfile.margins)}</p>
                  </div>
                </div>
                {editedData.financialProfile.revenueSegments?.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Revenue Segments:</span>
                    <ul className="list-disc list-inside text-neutral-900 mt-1">
                      {editedData.financialProfile.revenueSegments.map((seg: any, idx: number) => (
                        <li key={idx}>{typeof seg === 'string' ? seg : `${seg.segment}: ${seg.percentage}%`}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Business Structure */}
      {editedData.businessStructure && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">🏢 Business Structure</h4>
            <button
              onClick={() => setEditingSection(editingSection === 'structure' ? null : 'structure')}
              className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
            >
              {editingSection === 'structure' ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              {editingSection === 'structure' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="space-y-3">
            {editingSection === 'structure' ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">Business Units</label>
                  {(editedData.businessStructure.businessUnits || []).map((unit: any, idx: number) => {
                    const unitObj = typeof unit === 'string' ? { name: unit } : unit;
                    return (
                      <div key={idx} className="border border-neutral-200 rounded-lg p-3 mb-3 bg-neutral-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-medium text-neutral-500">Business Unit {idx + 1}</span>
                          <button
                            onClick={() => handleArrayRemove('businessStructure', 'businessUnits', idx)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          <Input
                            label="Name"
                            value={unitObj.name || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'businessUnits', idx, 'name', e.target.value)}
                          />
                          <Input
                            label="Revenue"
                            placeholder="e.g., $28.0 billion"
                            value={unitObj.revenue || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'businessUnits', idx, 'revenue', e.target.value)}
                          />
                          <Input
                            label="Operating Income"
                            placeholder="e.g., $8.5 billion"
                            value={unitObj.operatingIncome || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'businessUnits', idx, 'operatingIncome', e.target.value)}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              label="Gross Margin"
                              placeholder="e.g., 64.5%"
                              value={unitObj.grossMargin || ''}
                              onChange={(e) => handleObjectFieldEdit('businessStructure', 'businessUnits', idx, 'grossMargin', e.target.value)}
                            />
                            <Input
                              label="Operating Margin"
                              placeholder="e.g., 30.2%"
                              value={unitObj.operatingMargin || ''}
                              onChange={(e) => handleObjectFieldEdit('businessStructure', 'businessUnits', idx, 'operatingMargin', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleArrayAdd('businessStructure', 'businessUnits')}
                  >
                    Add Business Unit
                  </Button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">Product Lines</label>
                  {(editedData.businessStructure.productLines || []).map((line: any, idx: number) => {
                    const lineObj = typeof line === 'string' ? { name: line } : line;
                    return (
                      <div key={idx} className="border border-neutral-200 rounded-lg p-3 mb-3 bg-neutral-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-medium text-neutral-500">Product Line {idx + 1}</span>
                          <button
                            onClick={() => handleArrayRemove('businessStructure', 'productLines', idx)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          <Input
                            label="Name"
                            value={lineObj.name || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'productLines', idx, 'name', e.target.value)}
                          />
                          <Input
                            label="Revenue"
                            placeholder="e.g., $50.2 billion"
                            value={lineObj.revenue || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'productLines', idx, 'revenue', e.target.value)}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              label="Revenue %"
                              placeholder="e.g., 42%"
                              value={lineObj.revenuePercentage || ''}
                              onChange={(e) => handleObjectFieldEdit('businessStructure', 'productLines', idx, 'revenuePercentage', e.target.value)}
                            />
                            <Input
                              label="Growth Rate"
                              placeholder="e.g., 15%"
                              value={lineObj.growthRate || ''}
                              onChange={(e) => handleObjectFieldEdit('businessStructure', 'productLines', idx, 'growthRate', e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleArrayAdd('businessStructure', 'productLines')}
                  >
                    Add Product Line
                  </Button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-3">Geographic Regions</label>
                  {((editedData.businessStructure.geographicRegions || editedData.businessStructure.geographicFootprint) || []).map((region: any, idx: number) => {
                    const regionObj = typeof region === 'string' ? { name: region } : region;
                    return (
                      <div key={idx} className="border border-neutral-200 rounded-lg p-3 mb-3 bg-neutral-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-medium text-neutral-500">Geographic Region {idx + 1}</span>
                          <button
                            onClick={() => handleArrayRemove('businessStructure', 'geographicRegions', idx)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="space-y-2">
                          <Input
                            label="Name"
                            value={regionObj.name || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'geographicRegions', idx, 'name', e.target.value)}
                          />
                          <Input
                            label="Revenue"
                            placeholder="e.g., $162.6 billion"
                            value={regionObj.revenue || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'geographicRegions', idx, 'revenue', e.target.value)}
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              label="Revenue %"
                              placeholder="e.g., 45%"
                              value={regionObj.revenuePercentage || ''}
                              onChange={(e) => handleObjectFieldEdit('businessStructure', 'geographicRegions', idx, 'revenuePercentage', e.target.value)}
                            />
                            <Input
                              label="Growth Rate"
                              placeholder="e.g., 8%"
                              value={regionObj.growthRate || ''}
                              onChange={(e) => handleObjectFieldEdit('businessStructure', 'geographicRegions', idx, 'growthRate', e.target.value)}
                            />
                          </div>
                          <Input
                            label="Operating Income"
                            placeholder="e.g., $45.2 billion"
                            value={regionObj.operatingIncome || ''}
                            onChange={(e) => handleObjectFieldEdit('businessStructure', 'geographicRegions', idx, 'operatingIncome', e.target.value)}
                          />
                        </div>
                      </div>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleArrayAdd('businessStructure', 'geographicRegions')}
                  >
                    Add Region
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {editedData.businessStructure.businessUnits?.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600 block mb-2">Business Units:</span>
                    <div className="space-y-2">
                      {editedData.businessStructure.businessUnits.map((unit: any, idx: number) => (
                        <div key={idx} className="bg-neutral-50 border border-neutral-200 rounded p-2">
                          <div className="font-medium text-neutral-900 text-sm">
                            {typeof unit === 'string' ? unit : unit.name}
                          </div>
                          {typeof unit === 'object' && (unit.revenue || unit.operatingIncome || unit.grossMargin || unit.operatingMargin) && (
                            <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                              {unit.revenue && <div><span className="text-neutral-500">Revenue:</span> <span className="text-neutral-700">{unit.revenue}</span></div>}
                              {unit.operatingIncome && <div><span className="text-neutral-500">Op. Income:</span> <span className="text-neutral-700">{unit.operatingIncome}</span></div>}
                              {unit.grossMargin && <div><span className="text-neutral-500">Gross Margin:</span> <span className="text-neutral-700">{unit.grossMargin}</span></div>}
                              {unit.operatingMargin && <div><span className="text-neutral-500">Op. Margin:</span> <span className="text-neutral-700">{unit.operatingMargin}</span></div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {editedData.businessStructure.productLines?.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600 block mb-2">Product Lines:</span>
                    <div className="space-y-2">
                      {editedData.businessStructure.productLines.map((product: any, idx: number) => (
                        <div key={idx} className="bg-neutral-50 border border-neutral-200 rounded p-2">
                          <div className="font-medium text-neutral-900 text-sm">
                            {typeof product === 'string' ? product : product.name}
                          </div>
                          {typeof product === 'object' && (product.revenue || product.revenuePercentage || product.growthRate) && (
                            <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                              {product.revenue && <div><span className="text-neutral-500">Revenue:</span> <span className="text-neutral-700">{product.revenue}</span></div>}
                              {product.revenuePercentage && <div><span className="text-neutral-500">% of Total:</span> <span className="text-neutral-700">{product.revenuePercentage}</span></div>}
                              {product.growthRate && <div><span className="text-neutral-500">Growth:</span> <span className="text-neutral-700">{product.growthRate}</span></div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(editedData.businessStructure.geographicFootprint?.length > 0 || editedData.businessStructure.geographicRegions?.length > 0) && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600 block mb-2">Geographic Regions:</span>
                    <div className="space-y-2">
                      {(editedData.businessStructure.geographicRegions || editedData.businessStructure.geographicFootprint || []).map((region: any, idx: number) => (
                        <div key={idx} className="bg-neutral-50 border border-neutral-200 rounded p-2">
                          <div className="font-medium text-neutral-900 text-sm">
                            {typeof region === 'string' ? region : region.name}
                          </div>
                          {typeof region === 'object' && (region.revenue || region.revenuePercentage || region.growthRate || region.operatingIncome) && (
                            <div className="mt-1 grid grid-cols-2 gap-1 text-xs">
                              {region.revenue && <div><span className="text-neutral-500">Revenue:</span> <span className="text-neutral-700">{region.revenue}</span></div>}
                              {region.revenuePercentage && <div><span className="text-neutral-500">% of Total:</span> <span className="text-neutral-700">{region.revenuePercentage}</span></div>}
                              {region.growthRate && <div><span className="text-neutral-500">Growth:</span> <span className="text-neutral-700">{region.growthRate}</span></div>}
                              {region.operatingIncome && <div><span className="text-neutral-500">Op. Income:</span> <span className="text-neutral-700">{region.operatingIncome}</span></div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Operational Profile */}
      {editedData.operationalProfile && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">⚙️ Operational Profile</h4>
            <button
              onClick={() => setEditingSection(editingSection === 'operational' ? null : 'operational')}
              className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
            >
              {editingSection === 'operational' ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              {editingSection === 'operational' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="space-y-3">
            {editingSection === 'operational' ? (
              <>
                <Textarea
                  label="Manufacturing"
                  value={editedData.operationalProfile.manufacturing || ''}
                  onChange={(e) => handleSectionEdit('operationalProfile', 'manufacturing', e.target.value)}
                  rows={2}
                />
                <Textarea
                  label="Supply Chain"
                  value={editedData.operationalProfile.supplyChain || ''}
                  onChange={(e) => handleSectionEdit('operationalProfile', 'supplyChain', e.target.value)}
                  rows={2}
                />
              </>
            ) : (
              <>
                {editedData.operationalProfile.manufacturing && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Manufacturing:</span>
                    <p className="text-neutral-900">{editedData.operationalProfile.manufacturing}</p>
                  </div>
                )}
                {editedData.operationalProfile.supplyChain && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Supply Chain:</span>
                    <p className="text-neutral-900">{editedData.operationalProfile.supplyChain}</p>
                  </div>
                )}
                {editedData.operationalProfile.distributionChannels?.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Distribution Channels:</span>
                    <p className="text-neutral-900">{editedData.operationalProfile.distributionChannels.join(', ')}</p>
                  </div>
                )}
                {editedData.operationalProfile.customerSegments?.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Customer Segments:</span>
                    <p className="text-neutral-900">{editedData.operationalProfile.customerSegments.join(', ')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Organizational Scale */}
      {editedData.organizationalScale && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">📊 Organizational Scale</h4>
            <button
              onClick={() => setEditingSection(editingSection === 'scale' ? null : 'scale')}
              className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
            >
              {editingSection === 'scale' ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              {editingSection === 'scale' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="space-y-3">
            {editingSection === 'scale' ? (
              <>
                <Input
                  label="Employee Count"
                  value={extractValue(editedData.organizationalScale.employeeCount)}
                  onChange={(e) => handleSectionEdit('organizationalScale', 'employeeCount', e.target.value)}
                />
                <Input
                  label="Facilities"
                  value={extractValue(editedData.organizationalScale.facilities)}
                  onChange={(e) => handleSectionEdit('organizationalScale', 'facilities', e.target.value)}
                />
                <Input
                  label="Customer Base"
                  value={extractValue(editedData.organizationalScale.customerBase)}
                  onChange={(e) => handleSectionEdit('organizationalScale', 'customerBase', e.target.value)}
                />
                <Input
                  label="Supplier Base"
                  value={extractValue(editedData.organizationalScale.supplierBase)}
                  onChange={(e) => handleSectionEdit('organizationalScale', 'supplierBase', e.target.value)}
                />
              </>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                {editedData.organizationalScale.employeeCount && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Employees:</span>
                    <p className="text-neutral-900">{extractValue(editedData.organizationalScale.employeeCount)}</p>
                  </div>
                )}
                {editedData.organizationalScale.facilities && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Facilities:</span>
                    <p className="text-neutral-900">{extractValue(editedData.organizationalScale.facilities)}</p>
                  </div>
                )}
                {editedData.organizationalScale.customerBase && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Customer Base:</span>
                    <p className="text-neutral-900">{extractValue(editedData.organizationalScale.customerBase)}</p>
                  </div>
                )}
                {editedData.organizationalScale.supplierBase && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Supplier Base:</span>
                    <p className="text-neutral-900">{extractValue(editedData.organizationalScale.supplierBase)}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Industry Context */}
      {editedData.industryContext && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">🏭 Industry Context</h4>
            <button
              onClick={() => setEditingSection(editingSection === 'industry' ? null : 'industry')}
              className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1"
            >
              {editingSection === 'industry' ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
              {editingSection === 'industry' ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="space-y-3">
            {editingSection === 'industry' ? (
              <>
                <Input
                  label="Vertical"
                  value={editedData.industryContext.vertical || ''}
                  onChange={(e) => handleSectionEdit('industryContext', 'vertical', e.target.value)}
                />
                <Input
                  label="Sub-sector"
                  value={editedData.industryContext.subSector || ''}
                  onChange={(e) => handleSectionEdit('industryContext', 'subSector', e.target.value)}
                />
              </>
            ) : (
              <>
                {editedData.industryContext.vertical && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Industry:</span>
                    <p className="text-neutral-900">{editedData.industryContext.vertical}</p>
                  </div>
                )}
                {editedData.industryContext.subSector && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Sub-sector:</span>
                    <p className="text-neutral-900">{editedData.industryContext.subSector}</p>
                  </div>
                )}
                {editedData.industryContext.regulations?.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-neutral-600">Regulations:</span>
                    <p className="text-neutral-900">{editedData.industryContext.regulations.join(', ')}</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Strategic Initiatives */}
      {editedData.strategicInitiatives && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">🎯 Strategic Initiatives</h4>
          </div>

          <div className="space-y-3">
            {editedData.strategicInitiatives.growthInitiatives?.length > 0 && (
              <div>
                <span className="text-sm font-medium text-neutral-600">Growth Initiatives:</span>
                <ul className="list-disc list-inside text-neutral-900 mt-1">
                  {editedData.strategicInitiatives.growthInitiatives.map((init: string, idx: number) => (
                    <li key={idx}>{init}</li>
                  ))}
                </ul>
              </div>
            )}
            {editedData.strategicInitiatives.transformationPrograms?.length > 0 && (
              <div>
                <span className="text-sm font-medium text-neutral-600">Transformation Programs:</span>
                <ul className="list-disc list-inside text-neutral-900 mt-1">
                  {editedData.strategicInitiatives.transformationPrograms.map((prog: string, idx: number) => (
                    <li key={idx}>{prog}</li>
                  ))}
                </ul>
              </div>
            )}
            {editedData.strategicInitiatives.challenges?.length > 0 && (
              <div>
                <span className="text-sm font-medium text-neutral-600">Challenges:</span>
                <ul className="list-disc list-inside text-neutral-900 mt-1">
                  {editedData.strategicInitiatives.challenges.map((challenge: string, idx: number) => (
                    <li key={idx}>{challenge}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Data Complexity */}
      {editedData.dataComplexity && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-white">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-md font-semibold text-neutral-900">🔍 Data Complexity</h4>
          </div>

          <div className="space-y-2">
            {editedData.dataComplexity.scope && (
              <div>
                <span className="text-sm font-medium text-neutral-600">Scope:</span>
                <p className="text-neutral-900">{editedData.dataComplexity.scope}</p>
              </div>
            )}
            {editedData.dataComplexity.productComplexity && (
              <div>
                <span className="text-sm font-medium text-neutral-600">Product Complexity:</span>
                <p className="text-neutral-900">{editedData.dataComplexity.productComplexity}</p>
              </div>
            )}
            {editedData.dataComplexity.multiChannel !== undefined && (
              <div>
                <span className="text-sm font-medium text-neutral-600">Multi-Channel:</span>
                <p className="text-neutral-900">{editedData.dataComplexity.multiChannel ? 'Yes' : 'No'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sources */}
      {editedData.sources?.length > 0 && (
        <div className="border border-neutral-200 rounded-lg p-4 bg-neutral-50">
          <h4 className="text-md font-semibold text-neutral-900 mb-2">📚 Sources</h4>
          <ul className="space-y-1 text-sm text-neutral-700">
            {editedData.sources.map((source: string, idx: number) => (
              <li key={idx} className="truncate">
                {idx + 1}. <a href={source} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{source}</a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={() => onSave(editedData)}
          loading={isSaving}
          disabled={isSaving}
        >
          <Check className="w-4 h-4" />
          Save to Database
        </Button>
      </div>
    </div>
  );
}
